import { watch, onUnmounted, type Ref, type ComputedRef } from 'vue'

/** Stable id generator (no need for crypto in tests / SSR edge cases) */
let idSeq = 0
function nextOverlayId(): string {
  return `exclusive-overlay-${++idSeq}`
}

const closers = new Map<string, () => void>()
let activeId: string | null = null

/**
 * Close whichever overlay currently owns the exclusive layer (if any).
 * Does not change activeId if the closer no-ops (e.g. already closed).
 */
export function closeExclusiveOverlay(): void {
  if (activeId === null) return
  const close = closers.get(activeId)
  close?.()
}

function notifyOpened(id: string): void {
  if (activeId !== null && activeId !== id) {
    closers.get(activeId)?.()
  }
  activeId = id
}

function notifyClosed(id: string): void {
  if (activeId === id) {
    activeId = null
  }
}

function register(id: string, close: () => void): void {
  closers.set(id, close)
}

function unregister(id: string): void {
  closers.delete(id)
  if (activeId === id) {
    activeId = null
  }
}

type OpenSource = Ref<boolean> | ComputedRef<boolean>

export interface UseExclusiveOverlayOptions {
  /** Reactive open state (read for watch). */
  isOpen: OpenSource
  /** Called when another overlay wins; must set this instance to closed (same path as normal close). */
  close: () => void
}

/**
 * Ensures only one registered overlay is open at a time across the app.
 * Register in components that own custom dropdown / popover panels (not native select elements).
 */
export function useExclusiveOverlay(options: UseExclusiveOverlayOptions): void {
  const id = nextOverlayId()
  const { isOpen, close } = options

  // Register synchronously so `immediate` watch runs after this instance is known to the coordinator.
  register(id, close)
  onUnmounted(() => {
    unregister(id)
  })

  watch(
    isOpen,
    (open) => {
      if (open) {
        notifyOpened(id)
      } else {
        notifyClosed(id)
      }
    },
    { flush: 'sync', immediate: true }
  )
}
